import { AwaitContainer, Badge, Table, type TableColumn } from '@ibsys/kit';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type DownloadTemplateEntityType,
  type DownloadTemplateVariable,
  useDownloadTemplateVariables,
} from '@/entities/download-template';

type Props = {
  /** Сущность открытой вкладки: показываются только её переменные */
  entityType: DownloadTemplateEntityType;
};

/**
 * Подсказка по сущности: какие переменные система подставляет в шаблон, что означает каждая и
 * как их записывают в файле. Перечень ведёт сам код подстановки на бэкенде, поэтому подсказка не
 * расходится с тем, что подставляется.
 */
export const DownloadTemplateVariablesHelp: FC<Props> = ({ entityType }) => {
  const { t } = useTranslation('downloadTemplates');
  const { data, isLoading } = useDownloadTemplateVariables(entityType);

  const columns: TableColumn<DownloadTemplateVariable & { id: string }>[] = [
    {
      accessorKey: 'name',
      header: () => <span>{t('variables.name')}</span>,
      cell: ({ row }) => <code className="text-sm">{`{{ ${row.original.name} }}`}</code>,
    },
    {
      accessorKey: 'description',
      header: () => <span>{t('variables.description')}</span>,
      cellClassName: () => 'whitespace-normal',
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <span>{row.original.description}</span>
          {row.original.isList && (
            <Badge variant="secondary" outline className="self-start">
              {t('variables.isList')}
            </Badge>
          )}
          {row.original.fields?.length ? (
            <ul className="flex flex-col gap-0.5 text-sm text-muted-foreground">
              {row.original.fields.map((field) => (
                <li key={field.name}>{`${row.original.name}.${field.name} — ${field.description}`}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ),
    },
  ];

  const variables = (data?.variables ?? []).map((variable) => ({ ...variable, id: variable.name }));

  return (
    <AwaitContainer isLoading={isLoading}>
      <div className="flex flex-col gap-6">
        <Table columns={columns} data={variables} emptyMessage={t('variables.empty')} />

        <div className="flex flex-col gap-3">
          <h3 className="font-medium">{t('variables.syntaxTitle')}</h3>
          {(data?.syntax ?? []).map((example) => (
            <div key={example.title} className="flex flex-col gap-1 rounded-lg border border-border p-3">
              <span className="font-medium">{example.title}</span>
              <code className="text-sm">{example.example}</code>
              <span className="text-sm text-muted-foreground">{example.description}</span>
            </div>
          ))}
        </div>
      </div>
    </AwaitContainer>
  );
};
